// @flow
import React, {Component} from 'react';
import {
  FlatList,
  Image,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import runAfterInteractions from '../../utils/runAfterInteractions';
import {Button, Text} from 'trpc-react-native-ui';
import {FullButton} from '../../components/common';
import PaymentAuthentications from '../../components/payments/PaymentAuthentications';
import PaymentListViewRow from '../../components/payments/PaymentListViewRow';
import I18n from '../../lib/i18n';
import {
  formatPaymentMeanInfo,
  paymentMeanValidityStatus,
} from '../../lib/paymentMeansFormatter';
import theme from '../../theme-config';

type Props = {
  fetchPayments: Function,
  fetchPromoProductsRequest: Function,
  hasUnauthenticatedPayment: boolean,
  hasBillingAddress: any,
  isRefreshingPaymentDetails: boolean,
  isRefreshingPaymentHistory: boolean,
  paymentMean?: Object,
  payments: Object[],
  registerNewPayment: Function,
};

class Payments extends Component<Props> {
  styles = getStyles();

  componentDidMount() {
    runAfterInteractions(() => {
      this.props.fetchPayments();
      this.props.fetchPromoProductsRequest();
    });
  }

  renderEmptyPaymentListHistory = () => (
    <Text style={this.styles.emptyText}>
      {I18n.t('Dashboard.Payments.noPaymentHistory')}
    </Text>
  );

  renderItem = ({item}: Object) => {
    const paymentKey = item.paymentRequests[0] ? 'paymentRequests' : 'balances';
    const paymentId = item[paymentKey]?.[0]?.transactionId;

    return (
      <PaymentListViewRow
        key={paymentId}
        amount={item[paymentKey]?.[0]?.amount}
        navigation={this.props.navigation}
        paidItems={item.paidItems}
        paymentId={paymentId}
        paymentMeanOuid={item[paymentKey]?.[0]?.paymentMeanOuid}
        status={item[paymentKey]?.[0]?.status}
        timestamp={item[paymentKey]?.[0]?.dateTimeCreate}
        transactionId={item[paymentKey]?.[0]?.externalReference}
      />
    );
  };

  cardBrand = (network: String) => {
    let cardBrand;
    switch (network) {
      case 'mastercard':
        cardBrand = theme.mastercardLogo;
        break;
      case 'visa':
        cardBrand = theme.visaLogo;
        break;
      case 'cartes_bancaires':
        cardBrand = theme.cbLogo;
        break;
      default:
        cardBrand = theme.visaLogo;
        break;
    }

    return cardBrand;
  };

  keyExtractor = (item: Object) => item?.paymentRequests?.[0]?.ouid;

  renderPaymentMeanDetails = (
    paymentMeanInfo: Object,
    paymentMeanStatus: string,
  ) => {
    const styles = this.styles;
    const {cardNumber, expirationDate, type, preferredNetwork} =
      paymentMeanInfo;
    let imageElement;
    let textElement;

    switch (type) {
      case 'PAYPAL':
        imageElement = (
          <Image
            style={styles.paypalLogo}
            source={theme.paypalLogo}
            resizeMode="contain"
          />
        );
        break;
      case 'CREDIT_CARD':
      default: {
        if (paymentMeanStatus === 'DELETED') {
          break;
        }

        const cardInfosStyle =
          paymentMeanStatus === 'VALID' ? styles.text : styles.warningText;

        imageElement = (
          <Image
            style={styles.visaLogo}
            source={this.cardBrand(preferredNetwork)}
            resizeMode="contain"
          />
        );
        textElement = (
          <>
            <Text style={cardInfosStyle}>{`***`}</Text>
            <Text style={[cardInfosStyle, styles.cardNumber]}>
              {cardNumber.substr(cardNumber.length - 4)}
            </Text>
            <Text style={cardInfosStyle}>{expirationDate}</Text>
          </>
        );
        break;
      }
    }
    return (
      <View style={styles.paymentMeanDetailsContainer}>
        {imageElement}
        {textElement}
      </View>
    );
  };

  renderAddPaymentMeanButton = () => {
    const styles = this.styles;
    const {registerNewPayment, isRefreshingPaymentDetails} = this.props;

    return (
      <FullButton
        text={I18n.t('Dashboard.Payments.addPaymentMean')}
        onPress={registerNewPayment}
        style={styles.addPaymentMeanButton}
        textStyle={styles.addPaymentMeanButtonText}
        isLoading={isRefreshingPaymentDetails}
        disabled={!this.props.hasBillingAddress}
      />
    );
  };

  renderPaymentMean = (paymentMean: Object) => {
    const styles = this.styles;

    const {characteristics, endDateTime} = paymentMean;
    const paymentMeanInfo = formatPaymentMeanInfo(characteristics);

    const paymentMeanStatus = paymentMeanValidityStatus(
      endDateTime,
      paymentMeanInfo.expirationDate,
    );

    const invalidLabelPath =
      paymentMeanStatus === 'EXPIRED'
        ? 'Dashboard.Payments.expiredCard'
        : 'Dashboard.Payments.noPaymentMean';

    return (
      <View
      key={'renderPaymentMeanKey'}
        style={[
          styles.paymentMeanContainer,
          !this.props.hasUnauthenticatedPayment &&
            styles.paymentMeanContainerPaddingTop,
        ]}>
        <Text
          style={[
            styles.title,
            paymentMeanStatus === 'DELETED' && styles.noPaymentMeanText,
          ]}>
          {I18n.t('Dashboard.Payments.paymentMean')}
        </Text>
        <View style={styles.paymentMeanInfoContainer}>
          {this.renderPaymentMeanDetails(paymentMeanInfo, paymentMeanStatus)}
          {paymentMeanStatus !== 'DELETED' && (
            <Button
              onPress={this.props.registerNewPayment}
              style={styles.changePaymentMeanButton}>
              <Text style={styles.changePaymentMeanButtonText}>
                {I18n.t('Dashboard.Payments.changePaymentMean')}
              </Text>
            </Button>
          )}
        </View>
        {paymentMeanStatus !== 'VALID' && (
          <>
            <Text
              style={[
                styles.paymentMeanStatus,
                paymentMeanStatus === 'DELETED' && styles.noPaymentMeanText,
              ]}>
              {I18n.t(invalidLabelPath)}
            </Text>
            {this.renderAddPaymentMeanButton()}
          </>
        )}
      </View>
    );
  };

  renderNoPaymentMean = () => {
    const styles = this.styles;

    return (
      <View style={styles.noPaymentMeanContainer}>
        <Text style={[styles.title, styles.textSpacing]}>
          {I18n.t('Dashboard.Payments.paymentMean')}
        </Text>
        <Text style={[styles.warningText, styles.textSpacing]}>
          {I18n.t('Dashboard.Payments.noPaymentMean')}
        </Text>
        {this.renderAddPaymentMeanButton()}
      </View>
    );
  };

  render() {
    const styles = this.styles;
    const {paymentMean, payments, isRefreshingPaymentHistory, fetchPayments} =
      this.props;
    if (!paymentMean || !paymentMean.characteristics) {
      return this.renderNoPaymentMean();
    }

    const getContentContainerStyle =
      !(payments && payments.length) && styles.emptyContainer;

    const WrapperView = this.props.hasUnauthenticatedPayment
      ? ScrollView
      : View;

    return (
      <WrapperView style={styles.parentContainer}>
        <PaymentAuthentications key={'paymentAuthenticationKey'} withSeparator={false} />
        {this.renderPaymentMean(paymentMean)}
        <Text key={'paymentsTitle'} style={styles.listTitle}>{I18n.t('payments')}</Text>
        <FlatList
        key={'paymentsDataList'}
          data={payments}
          ListEmptyComponent={this.renderEmptyPaymentListHistory}
          refreshing={isRefreshingPaymentHistory}
          onRefresh={fetchPayments}
          renderItem={this.renderItem}
          keyExtractor={this.keyExtractor}
          contentContainerStyle={getContentContainerStyle}
          extraData={{now: Math.trunc(Date.now() / 15000)}} // date is necessary to rerender "time ago" (15sec delay)
        />
      </WrapperView>
    );
  }
}

const getStyles = () =>
  StyleSheet.create({
    addPaymentMeanButton: {
      alignSelf: 'center',
      minWidth: 270,
      paddingHorizontal: 10,
    },
    addPaymentMeanButtonText: {
      color: theme.white,
      fontSize: 16,
      fontWeight: '500',
    },
    cardInfoContainer: {
      display: 'flex',
      flex: 3,
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    cardNumber: {
      marginRight: 20,
      fontWeight: 'bold',
    },
    changePaymentMeanButton: {
      padding: 10,
    },
    changePaymentMeanButtonText: {
      color: theme.primaryColor,
      fontSize: 14,
      fontWeight: '500',
    },
    disabled: {
      color: '#ddd',
    },
    emptyContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      height: '70%',
    },
    emptyText: {
      color: theme.emptyTextColor,
      fontSize: 13,
    },
    paypalLogo: {height: 40, width: 75},
    visaLogo: {marginRight: 20, height: 40, width: 45},
    listTitle: {
      fontSize: 16,
      fontWeight: '500',
      marginVertical: 24,
      marginLeft: 20,
    },
    noPaymentMeanContainer: {
      alignItems: 'center',
      display: 'flex',
      height: '80%',
      justifyContent: 'center',
    },
    noPaymentMeanText: {
      textAlign: 'center',
    },
    parentContainer: {
      backgroundColor: theme.backgroundColor,
      flex: 1,
    },
    paymentMeanContainer: {
      borderBottomColor: theme.gray.light,
      borderBottomWidth: 1,
      display: 'flex',
      paddingHorizontal: 20,
      paddingBottom: 48,
      justifyContent: 'center',
    },
    paymentMeanContainerPaddingTop: {
      paddingTop: 48,
    },
    paymentMeanDetailsContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    paymentMeanInfoContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginVertical: 10,
    },
    paymentMeanStatus: {
      color: theme.primaryColor,
      fontSize: 14,
      marginBottom: 30,
    },
    text: {
      color: theme.gray.normal,
      fontSize: 13,
    },
    textSpacing: {
      marginBottom: 20,
    },
    title: {
      fontSize: 16,
      fontWeight: '500',
      marginBottom: 10,
    },
    warningText: {
      color: theme.primaryColor,
      fontSize: 13,
    },
  });

export default Payments;
